// Frontend crypto helper that matches backend encode/decode:
// - AES-256-GCM
// - IV length: 12 bytes
// - Auth tag length: 16 bytes (128-bit)
// - Key derivation:
//   - If ENCRYPTION_KEY is 64 hex chars -> use as raw 32-byte key
//   - Else if UTF-8 bytes are exactly 32 -> use as raw 32-byte key
//   - Else -> SHA-256(ENCRYPTION_KEY) as 32-byte key
//
// NOTE:
// This is intended to "sync" with your backend scheme. It does NOT replace TLS.
// Putting the encryption key in the frontend means a determined user can extract it.

const ALGO = 'AES-GCM';
const IV_LEN = 12;
const AUTH_TAG_LEN = 16;
const KEY_LEN = 32;

function hasWebCrypto() {
    return typeof globalThis !== 'undefined' && !!globalThis.crypto?.subtle;
}

function getEnvKeyRaw() {
    // Vite only exposes variables prefixed with VITE_.
    // Keep a fallback for custom setups/tests.
    // IMPORTANT: keep these as direct property reads so Vite can inline them.
    // Optional chaining / dynamic access can prevent replacement in some builds.
    const viteKey = import.meta.env.VITE_ENCRYPTION_KEY;
    const nonViteKey = import.meta.env.ENCRYPTION_KEY;
    return viteKey || nonViteKey || undefined;
}

function hexToBytes(hex) {
    const clean = hex.trim();
    const out = new Uint8Array(clean.length / 2);
    for (let i = 0; i < out.length; i++) {
        out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
    }
    return out;
}

function bytesToBase64(bytes) {
    // Chunk to avoid call stack limits.
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        const chunk = bytes.subarray(i, i + chunkSize);
        binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
}

function base64ToBytes(b64) {
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
}

async function sha256Bytes(utf8Bytes) {
    const digest = await crypto.subtle.digest('SHA-256', utf8Bytes);
    return new Uint8Array(digest);
}

let cachedKeyPromise = null;

async function getCryptoKey() {
    if (cachedKeyPromise) return cachedKeyPromise;

    cachedKeyPromise = (async () => {
        if (!hasWebCrypto()) {
            const err = new Error('WebCrypto is not available in this browser environment');
            err.code = 'CRYPTO_UNSUPPORTED';
            throw err;
        }

        const raw = getEnvKeyRaw();
        if (!raw || typeof raw !== 'string') {
            const err = new Error('VITE_ENCRYPTION_KEY is not set; required for encode/decode');
            err.code = 'CRYPTO_KEY_MISSING';
            throw err;
        }

        let keyBytes;
        if (raw.length === 64 && /^[0-9a-fA-F]+$/.test(raw)) {
            keyBytes = hexToBytes(raw);
        } else {
            const enc = new TextEncoder();
            const utf8 = enc.encode(raw);
            keyBytes = utf8.length === KEY_LEN ? utf8 : await sha256Bytes(utf8);
        }

        if (keyBytes.length !== KEY_LEN) {
            // Defensive, should never happen
            const err = new Error('Invalid derived key length');
            err.code = 'CRYPTO_KEY_INVALID';
            throw err;
        }

        return crypto.subtle.importKey('raw', keyBytes, { name: ALGO }, false, ['encrypt', 'decrypt']);
    })().catch((err) => {
        // In dev (HMR), env might change; allow retry after failure.
        cachedKeyPromise = null;
        throw err;
    });

    return cachedKeyPromise;
}

/**
 * Encrypt a plaintext string for safe transit.
 * Output matches backend: base64( iv(12) + ciphertext + authTag(16) ).
 */
export async function encode(plaintext) {
    if (plaintext === null || plaintext === undefined) return plaintext;

    const str = typeof plaintext === 'string' ? plaintext : String(plaintext);
    const key = await getCryptoKey();
    const iv = crypto.getRandomValues(new Uint8Array(IV_LEN));
    const data = new TextEncoder().encode(str);

    // WebCrypto returns ciphertext with authTag appended at the end.
    const encrypted = await crypto.subtle.encrypt({ name: ALGO, iv, tagLength: AUTH_TAG_LEN * 8 }, key, data);
    const encBytes = new Uint8Array(encrypted);

    const combined = new Uint8Array(iv.length + encBytes.length);
    combined.set(iv, 0);
    combined.set(encBytes, iv.length);

    return bytesToBase64(combined);
}

/**
 * Decrypt a value previously encrypted with encode().
 * Throws CRYPTO_DECODE_FAILED on tamper/key mismatch.
 */
export async function decode(ciphertext) {
    if (ciphertext === null || ciphertext === undefined) return ciphertext;
    const key = await getCryptoKey();

    const buffer = base64ToBytes(String(ciphertext));
    if (buffer.length < IV_LEN + AUTH_TAG_LEN) {
        const err = new Error('Invalid encrypted payload');
        err.code = 'CRYPTO_DECODE_FAILED';
        throw err;
    }

    const iv = buffer.subarray(0, IV_LEN);
    const encPlusTag = buffer.subarray(IV_LEN); // includes authTag at end

    try {
        const decrypted = await crypto.subtle.decrypt(
            { name: ALGO, iv, tagLength: AUTH_TAG_LEN * 8 },
            key,
            encPlusTag
        );
        return new TextDecoder().decode(new Uint8Array(decrypted));
    } catch (e) {
        const err = new Error('Decryption failed; key mismatch or payload tampered');
        err.code = 'CRYPTO_DECODE_FAILED';
        err.cause = e;
        throw err;
    }
}

export async function encodeIfPresent(value) {
    if (value === null || value === undefined || value === '') return value;
    return encode(value);
}

export async function decodeIfPresent(value) {
    if (value === null || value === undefined || value === '') return value;
    if (typeof value !== 'string') return value;

    const trimmed = value.trim();
    // Mirror backend's conservative "looks encrypted" check
    if (trimmed.length < IV_LEN + AUTH_TAG_LEN) return value;

    try {
        return await decode(value);
    } catch {
        return value;
    }
}

/**
 * Encode only if value is not already encrypted with this scheme.
 * Prevents "double encryption" when values round-trip through UI.
 */
export async function encodeIfNotEncrypted(value) {
    if (value === null || value === undefined || value === '') return value;
    if (typeof value !== 'string') return encode(value);

    try {
        // If it decrypts with our key, assume it's already encrypted.
        await decode(value);
        return value;
    } catch {
        return encode(value);
    }
}

/**
 * Decode repeatedly (up to maxDepth) while it successfully decrypts.
 * Useful if a value was accidentally encrypted multiple times.
 */
export async function decodeDeepIfPresent(value, maxDepth = 3) {
    if (value === null || value === undefined || value === '') return value;
    if (typeof value !== 'string') return value;

    let current = value;
    for (let i = 0; i < maxDepth; i += 1) {
        try {
            const next = await decode(current);
            if (next === current) break;
            current = next;
        } catch {
            break;
        }
    }
    return current;
}

